import type { RecommendationsProps } from './Recommendations';
import type { ReactElement } from 'react';

import { useEffect, useState } from 'react';

import { RecommendationsComponent } from './RecommendationsComponent';
import { usePDPContext } from '../../../utilities/context/static/PDPContext';
import type { ProductRecommendations as RecommendationsSchema } from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { useApolloContext } from '~/campaign/core/context/ApolloContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Product } from '~/utilities/graphql/codegen';
import { useRecommendationsQuery } from '~/utilities/graphql/codegen';
import { fetchProducts } from '~/utilities/graphql/queries';

export const CompleteTheLook = (
  props: RecommendationsSchema & RecommendationsProps
): ReactElement => {
  const { locale, siteType } = useAppContext();
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const { client: apolloClient } = useApolloContext();
  const {
    product: { code },
  } = usePDPContext();
  const { accessibilityLabels } = useStaticContext();

  const { data } = useRecommendationsQuery({
    variables: {
      siteType,
      code,
      locale,
    },
    ssr: false,
    skip: !code,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (data?.recommendations?.productCodes) {
        const productCodes = data.recommendations.productCodes.split(',');
        const fetchedProducts = await fetchProducts({
          siteType,
          locale,
          codes: productCodes,
          apolloClient,
        });
        const ctlRecommendations = fetchedProducts?.products as Product[];

        if (ctlRecommendations) {
          const lowercaseProductCodes = productCodes.map(code => code.toLowerCase());
          const sortedProducts = [...ctlRecommendations].sort((a, b) => {
            const indexA = lowercaseProductCodes.indexOf(a.baseProduct?.toLocaleLowerCase() || '');
            const indexB = lowercaseProductCodes.indexOf(b.baseProduct?.toLocaleLowerCase() || '');

            if (indexA === -1) {
              return 1;
            }

            return indexA > indexB ? 1 : -1;
          });

          setRecommendedProducts(sortedProducts);
        }
      }
    };

    fetchData();
  }, [locale, data?.recommendations?.productCodes, siteType, apolloClient]);

  return (
    <RecommendationsComponent
      {...props}
      className="complete-the-look"
      ariaLabel={accessibilityLabels?.completeTheLook}
      recommendedProducts={recommendedProducts}
    />
  );
};
